// Clean route pattern -> its `/nova` alias pattern.
//
// Named after the A/B period, when a cohort user on a clean (then legacy) key was redirected to the
// mapped `/nova` twin. That redirect is gone: the redesign is served IN PLACE at every key below, and
// the `/nova` values survive only as aliases for old links and bookmarks. What the map still drives:
//   - `isRedesignRoute` (redesign chrome + background at the clean URL),
//   - the "no legacy modules under the redesign page" rule these routes must satisfy
//     (`redesignAbRoutes.test.ts`; see docs/redesign_default_rollout.md).
//
// Keys/values are `router.pathname` patterns (the `/pages` file path, e.g. `/e/[recordId]`), NOT
// resolved URLs. Dynamic segment names match 1:1 between each key and its alias (`[recordId]` /
// `[id]`), so `router.replace({ pathname, query })` fills them and carries over extra query params.
//
// Only routes whose page renders the redesign are listed. The legacy `/creators` list is absent (not
// migrated); the creator profile and its platform sub-pages (`/creators/twitch/:id` etc.) are all
// mirrored. `/design-preview` and `/drop-preview` are previews, handled directly in `isRedesignRoute`.
//
// NOTE: per `docs/redesign.md`, some twins still defer functionality to their legacy page (Bulk CSV
// on add-campaign + keys-and-products, In-Game rewards, my-keys "Keys for Giveaways", drop-admin
// audit approve/reject, campaigns one-click publish). Cohort users lose those on the redesign page;
// drop the relevant entry below to keep a page on legacy for everyone.
export const REDESIGN_AB_ROUTE_MAP = {
    '/drops': '/nova/drops',
    '/e/[recordId]': '/nova/drop/[recordId]',
    '/dare/[recordId]': '/nova/dare/[recordId]',
    '/creators/[recordId]': '/nova/creators/[recordId]',
    // Platform-specific profile URLs are mirrored 1:1 (structure preserved): each has its own nova
    // page rendering the same redesign profile, so `/creators/twitch/x` -> `/nova/creators/twitch/x`.
    '/creators/twitch/[recordId]': '/nova/creators/twitch/[recordId]',
    '/creators/yt/[recordId]': '/nova/creators/yt/[recordId]',
    '/creators/tiktok/[recordId]': '/nova/creators/tiktok/[recordId]',
    '/add-campaign': '/nova/add-campaign',
    '/campaign-dashboard': '/nova/campaign-dashboard',
    '/campaign-dashboard/[recordId]': '/nova/campaign-dashboard/[recordId]',
    '/campaign-clips': '/nova/campaign-clips',
    '/campaign-clips/[recordId]': '/nova/campaign-clips/[recordId]',
    '/campaign-creators': '/nova/campaign-creators',
    '/campaign-creators/[recordId]': '/nova/campaign-creators/[recordId]',
    '/applications': '/nova/applications',
    '/application/[id]': '/nova/application/[id]',
    '/brand-users': '/nova/brand-users',
    '/publisher-dares': '/nova/publisher-dares',
    '/whitelisted-creators': '/nova/whitelisted-creators',
    '/publisher-banned-creators': '/nova/banned-creators',
    '/keys-and-products': '/nova/keys-and-products',
    '/my-keys': '/nova/my-keys',
    '/drop-admin': '/nova/drop-admin',
    '/ban-admin': '/nova/ban-admin',
    '/featured-dares': '/nova/featured-dares',
    '/coupon-admin': '/nova/coupon-admin',
    '/short-url-analytics': '/nova/short-url-analytics',
    '/steam-sales': '/nova/steam-sales',
    '/account-managers': '/nova/account-managers',
    // Brand-gated in-app demo of the campaign dashboard. The demo recordId comes from
    // RecordIdOverrideProvider inside the page, so neither URL carries a `:recordId` segment.
    '/demo-campaign-dashboard': '/nova/demo-campaign-dashboard',
    '/creator-dashboard': '/nova/creator-dashboard',
    '/creators-leaderboard': '/nova/creators-leaderboard',
    '/review-clips': '/nova/review-clips',
    '/audience-targeting': '/nova/audience-targeting',
    '/review-project/[recordId]': '/nova/review-project/[recordId]',
    '/review-drop-project/[recordId]': '/nova/review-drop-project/[recordId]',
    '/pending-videos': '/nova/pending-videos',
    '/brand-approve-dares': '/nova/brand-approve-dares',
    '/brands-reports': '/nova/brands-reports',
    '/campaigns': '/nova/campaigns',
    '/privacy-policy': '/nova/privacy-policy',
    '/cookie-policy': '/nova/cookie-policy',
    '/terms': '/nova/terms',
    '/FAQ': '/nova/FAQ',
    // Sign in / sign up. Both render the same shared auth form the SignInDialog uses, one panel per
    // URL, so a link or an auth bounce to `/sign-in` lands on the redesign instead of the legacy form.
    '/sign-in': '/nova/sign-in',
    '/sign-up': '/nova/sign-up',
    // Password and email-change flows. All of these are destinations of transactional emails, so a
    // visitor reaches them cold and must not land on the old design.
    '/forgot-password': '/nova/forgot-password',
    '/reset-password': '/nova/reset-password',
    '/confirm-reset-password': '/nova/confirm-reset-password',
    '/transfer-account': '/nova/transfer-account',
    '/verify-new-email': '/nova/verify-new-email',
    '/email-verified': '/nova/email-verified',
    '/email-verification-failure': '/nova/email-verification-failure',
    '/set-email-failure': '/nova/set-email-failure',
    // Account verification flow. The redesign is now the default for everyone, so the server-side
    // `_app` redirect fires here too (it no longer depends on a cookie), including for the signed-out
    // fresh-signup case.
    '/verify-account': '/nova/verify-account',
    '/verify-account-finished': '/nova/verify-account-finished',
    '/verify-account-failure': '/nova/verify-account-failure',
    '/account-verified': '/nova/account-verified',
};
// Returns the redesign twin pattern for a legacy `router.pathname`, or undefined if the route
// has no twin (so the user stays on the legacy page).
export const getRedesignTwinPathname = (pathname) => REDESIGN_AB_ROUTE_MAP[pathname];
// Reverse of REDESIGN_AB_ROUTE_MAP: redesign twin pattern -> its legacy pattern. Used by the client
// A/B gate to send NON-cohort users out of a `/nova/*` route back to the legacy page (the mirror of
// the forward legacy -> redesign redirect). Redesign-only `/nova` pages absent from the map have no
// legacy twin, so the gate falls back to home for those.
const REDESIGN_AB_ROUTE_MAP_REVERSED = Object.entries(REDESIGN_AB_ROUTE_MAP)
    .reduce((acc, [legacyPathname, redesignPathname]) => {
    acc[redesignPathname] = legacyPathname;
    return acc;
}, {});
// Returns the legacy pattern for a redesign `/nova/*` `router.pathname`, or undefined if that
// redesign page has no legacy twin.
export const getLegacyTwinPathname = (pathname) => REDESIGN_AB_ROUTE_MAP_REVERSED[pathname];
// Resolve the twin to a concrete URL string for a legacy route pattern + its params/query.
// Used server-side (the `_app` getInitialProps redirect) where Next's object form
// `router.replace({ pathname, query })` is not available: substitutes the twin's `[param]` /
// `[...param]` segments from `query` and re-appends any leftover query params. Returns undefined
// when the route has no twin.
export const getRedesignTwinUrl = (pathname, query = {}) => {
    const twin = REDESIGN_AB_ROUTE_MAP[pathname];
    if (!twin) {
        return undefined;
    }
    const usedKeys = new Set();
    const path = twin.replace(/\[(?:\.\.\.)?([^\]]+)\]/g, (_match, name) => {
        usedKeys.add(name);
        const value = query[name];
        return Array.isArray(value) ? value.join('/') : (value !== null && value !== void 0 ? value : '');
    });
    const params = new URLSearchParams();
    Object.keys(query).forEach((key) => {
        if (usedKeys.has(key)) {
            return;
        }
        const value = query[key];
        if (Array.isArray(value)) {
            value.forEach(item => params.append(key, item));
        }
        else if (value !== undefined) {
            params.append(key, value);
        }
    });
    const queryString = params.toString();
    return queryString ? `${path}?${queryString}` : path;
};
